import React from "react";
import { Card, Table, Collapse, Typography, Space, Tag, Statistic, Empty, Tooltip } from "antd";
import { 
  SwapOutlined, 
  DollarCircleOutlined,
  FireOutlined,
  WalletOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  StarOutlined, // For Activity Score
  ExperimentOutlined, // For Attrition and Daily Attrition
  // StockOutlined, // No longer used for daily panel header directly for attrition
  // RiseOutlined, // Not directly used for attrition in panel header
  // FallOutlined, // Not directly used for attrition in panel header
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// 辅助函数，格式化大数字
const formatLargeNumber = (num, precision = 2) => {
  if (num === null || num === undefined || isNaN(num)) return 'N/A';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) return `${sign}${(absNum / 1e9).toFixed(precision)}B`;
  if (absNum >= 1e6) return `${sign}${(absNum / 1e6).toFixed(precision)}M`;
  if (absNum >= 1e3) return `${sign}${(absNum / 1e3).toFixed(precision)}K`;
  return `${sign}${absNum.toFixed(precision)}`;
};

// 辅助函数：计算单笔交易的代币净值变化 (USD) - 保持这个函数，表格中仍在使用
const calculateNetTokenValueChangeForTx = (transaction) => {
  if (!transaction || !transaction.token_transfers) {
    return 0;
  }
  let received_usd = 0;
  let sent_usd = 0;
  transaction.token_transfers.forEach(transfer => {
    if (transfer.value_usd && !isNaN(parseFloat(transfer.value_usd))) {
      if (transfer.direction === '买入' || transfer.direction === 'Receive') {
        received_usd += parseFloat(transfer.value_usd);
      } else if (transfer.direction === '卖出' || transfer.direction === 'Send') {
        sent_usd += parseFloat(transfer.value_usd);
      }
    }
  });
  return received_usd - sent_usd;
};

// 辅助函数：计算新的活跃分
const calculateActivityScore = (totalTxValueUsd) => {
  if (totalTxValueUsd === null || totalTxValueUsd === undefined || isNaN(totalTxValueUsd) || totalTxValueUsd < 128) {
    return 0;
  }
  return 6 + Math.floor(Math.log2(totalTxValueUsd / 64));
};

// 辅助函数：计算每日磨损
const calculateDailyAttrition = (transactions, targetSymbol = "BSC-USD") => {
  let firstSellQuantity = null;
  let lastBuyQuantity = null;

  if (!transactions || !Array.isArray(transactions)) {
    return 0;
  }

  for (const tx of transactions) {
    if (tx.token_transfers && Array.isArray(tx.token_transfers)) {
      for (const transfer of tx.token_transfers) {
        if (transfer.token_symbol === targetSymbol && 
            (transfer.direction === '卖出' || transfer.direction === 'Send') && 
            transfer.value !== null && !isNaN(parseFloat(transfer.value))) {
          if (firstSellQuantity === null) { // Ensure we only take the first one
            firstSellQuantity = parseFloat(transfer.value);
          }
        }
      }
    }
  }
  
  // Find last buy of targetSymbol
  // Iterate normally and overwrite, or iterate backwards and break.
  // For simplicity and clarity, iterating normally and overwriting:
  let tempLastBuyQuantity = null;
  for (const tx of transactions) {
    if (tx.token_transfers && Array.isArray(tx.token_transfers)) {
      for (const transfer of tx.token_transfers) {
         if (transfer.token_symbol === targetSymbol &&
            (transfer.direction === '买入' || transfer.direction === 'Receive') &&
            transfer.value !== null && !isNaN(parseFloat(transfer.value))) {
          tempLastBuyQuantity = parseFloat(transfer.value); // Keep updating to get the last one
        }
      }
    }
  }
  lastBuyQuantity = tempLastBuyQuantity;
  
  if (firstSellQuantity !== null && lastBuyQuantity !== null) {
    return firstSellQuantity - lastBuyQuantity;
  }
  // If only one side exists, or neither, attrition might be considered 0 or handled differently.
  // Based on "353.8减去...301.3", both must exist.
  // If firstSell is found but not lastBuy, attrition could be firstSellQuantity.
  // If lastBuy is found but not firstSell, attrition could be -lastBuyQuantity.
  // For now, strictly require both as per example.
  return 0; 
};

// 计算代币差值
const calculateTokenDifference = (transactions) => {
  if (!transactions || transactions.length < 2) return null;
  
  // 获取第一笔和最后一笔交易
  const firstTx = transactions[0];
  const lastTx = transactions[transactions.length - 1];
  
  if (!firstTx.token_transfers || !lastTx.token_transfers) return null;

  // 定义稳定币优先级
  const stableCoins = ['BSC-USD', 'USDC', 'USDT', 'BUSD', 'DAI'];
  
  // 在第一笔交易中寻找稳定币
  let targetSymbol = null;
  let firstValue = null;
  let lastValue = null;

  // 首先尝试找到两笔交易中都存在的稳定币
  for (const symbol of stableCoins) {
    const firstTransfer = firstTx.token_transfers.find(t => t.token_symbol === symbol);
    const lastTransfer = lastTx.token_transfers.find(t => t.token_symbol === symbol);
    
    if (firstTransfer && lastTransfer) {
      targetSymbol = symbol;
      firstValue = firstTransfer.value;
      lastValue = lastTransfer.value;
      break;
    }
  }

  // 如果没有找到共同的稳定币，使用第一笔交易中的第一个代币
  if (!targetSymbol && firstTx.token_transfers.length > 0) {
    const firstTransfer = firstTx.token_transfers[0];
    const symbol = firstTransfer.token_symbol;
    const lastTransfer = lastTx.token_transfers.find(t => t.token_symbol === symbol);
    
    if (lastTransfer) {
      targetSymbol = symbol;
      firstValue = firstTransfer.value;
      lastValue = lastTransfer.value;
    }
  }

  if (!targetSymbol || firstValue === null || lastValue === null) return null;
  
  return {
    difference: parseFloat(firstValue) - parseFloat(lastValue),
    symbol: targetSymbol
  };
};

const renderTokenTransfers = (token_transfers) => (
  <Table
    dataSource={token_transfers}
    columns={[
      { 
        title: "代币", 
        dataIndex: "token_name",
        render: (text, record) => (
          <Space direction="vertical" size="small">
            <Text strong>{text || '未知代币'}</Text>
            <Text type="secondary" style={{ fontSize: '0.85em' }}>{record.token_symbol || 'N/A'}</Text>
          </Space>
        )
      },
      { 
        title: "方向", 
        dataIndex: "direction",
        width: 80,
        align: 'center',
        render: (direction) => (
          <Tag 
            color={direction === '买入' || direction === 'Receive' ? 'success' : 'error'} 
            icon={direction === '买入' || direction === 'Receive' ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
            style={{fontSize: '0.9em', padding: '2px 8px'}}
          >
            {direction}
          </Tag>
        )
      },
      { 
        title: "数量", 
        dataIndex: "value",
        align: 'right',
        render: val => <Text strong>{typeof val === 'number' ? val.toFixed(4) : (val || 'N/A')}</Text>
      },
      { 
        title: "预估价值 (USD)", 
        dataIndex: "value_usd",
        align: 'right',
        render: v => (v !== null && v !== undefined && !isNaN(parseFloat(v))) ? (
          <Text style={{ color: '#00BFA5' }}>${formatLargeNumber(parseFloat(v))}</Text> 
        ) : <Text type="secondary">N/A</Text>
      }
    ]}
    rowKey={(record, idx) => `${record.token_contract_address || record.token_symbol || 'token'}-${idx}-${record.value}-${Math.random()}`} // Added Math.random for more uniqueness
    size="small"
    pagination={false}
    style={{ marginTop: 8, background: 'rgba(0,0,0,0.1)' }}
  />
);

export default function ResultTable({ results }) {
  if (!results || results.length === 0) {
    return (
      <Card style={{ marginTop: 32, textAlign: 'center' }} bordered={false}>
        <Empty description={<Text type="secondary" style={{fontSize: '1.1em'}}>暂无分析结果，请先添加并查询地址。</Text>} />
      </Card>
    );
  }

  const targetAttritionSymbol = "BSC-USD"; // Define the target symbol for attrition

  return (
    <div style={{ marginTop: 32 }}>
      {results.map((item, idx) => {
        // Total attrition for the address is no longer displayed in the Card's extra
        // let totalAttritionForAddress = 0;
        // if (item.stats && !item.error) {
        //   Object.values(item.stats).forEach(stat => {
        //     totalAttritionForAddress += calculateDailyAttrition(stat.transactions, targetAttritionSymbol);
        //   });
        // }

        return (
          <Card 
            key={item.address || idx} 
            title={
              <Space align="center">
                <WalletOutlined style={{ fontSize: 20, color: '#00A8E8' }} />
                <Text strong style={{ fontSize: '1.2em', color: '#00A8E8' }}>{item.address}</Text>
                {item.remark && (
                  <Tag color="cyan" style={{ marginLeft: 8 }}>{item.remark}</Tag>
                )}
              </Space>
            }
            // extra prop removed as per request to show daily attrition in panel
            bordered={false}
            style={{ marginBottom: 24 }}
            headStyle={{ borderBottom: '1px solid rgba(0, 168, 232, 0.3)'}}
          >
            {item.error ? (
              <Space direction="vertical" align="center" style={{width: '100%', padding: '20px'}}>
                <CloseCircleOutlined style={{fontSize: '32px', color: '#F44336'}}/>
                <Title level={5} type="danger">查询失败</Title>
                <Paragraph type="secondary">{item.error}</Paragraph>
              </Space>
            ) : !item.stats || Object.keys(item.stats).length === 0 ? (
              <Space direction="vertical" align="center" style={{width: '100%', padding: '20px'}}>
                <InfoCircleOutlined style={{fontSize: '32px', color: '#FFC107'}}/>
                <Title level={5} type="warning">无交易数据</Title>
                <Paragraph type="secondary">在选定日期范围内，该地址没有查询到交易记录。</Paragraph>
              </Space>
            ) : (
              <Collapse 
                bordered={false} 
                expandIconPosition="end"
                defaultActiveKey={Object.keys(item.stats).length > 0 ? Object.keys(item.stats)[0] : undefined}
                style={{ background: 'transparent' }}
              >
                {Object.entries(item.stats).map(([date, stat]) => {
                  const activityScore = calculateActivityScore(stat.total_tx_value_usd);
                  const dailyAttrition = calculateDailyAttrition(stat.transactions, targetAttritionSymbol);

                  return (
                    <Panel 
                      header={
                        <Space size="large" align="center" wrap style={{width: '100%', justifyContent: 'space-between'}}>
                          <Space>
                            <CalendarOutlined style={{color: '#00A8E8'}}/>
                            <Text strong style={{fontSize: '1.1em'}}>{date}</Text>
                          </Space>
                          <Space wrap>
                            <Tooltip title={`基于当日总交易额 (USD) 的活跃度评分。当前交易额: $${formatLargeNumber(stat.total_tx_value_usd || 0)}`}>
                              <Tag icon={<StarOutlined />} color="gold">
                                活跃分: {activityScore}
                              </Tag>
                            </Tooltip>
                            <Tag 
                              color={stat.success_transaction_count > 0 ? "success" : "default"}
                              icon={<CheckCircleOutlined />}
                            >
                              交易 {stat.success_transaction_count || 0} 笔
                            </Tag>
                             <Tooltip title={`当日 ${targetAttritionSymbol} 磨损量 (首次卖出 - 末次买入)`}>
                                <Tag 
                                  icon={<ExperimentOutlined />}
                                  color={dailyAttrition > 0 ? "error" : dailyAttrition < 0 ? "success" : "default"} // Positive attrition is a "loss"
                                >
                                  当日磨损 ({targetAttritionSymbol}): {formatLargeNumber(dailyAttrition, 4)}
                                </Tag>
                            </Tooltip>
                          </Space>
                        </Space>
                      } 
                      key={date}
                      style={{
                        marginBottom: 16,
                        border: '1px solid rgba(0, 168, 232, 0.2)',
                      }}
                    >
                      <Space wrap size={24} style={{ padding: '16px 0', justifyContent: 'space-around', width: '100%' }}>
                        <Statistic 
                          title={<Space><DollarCircleOutlined /> 预估总交易额 (USD)</Space>}
                          value={stat.total_tx_value_usd} 
                          formatter={(val) => `$${formatLargeNumber(parseFloat(val))}`}
                          valueStyle={{ color: '#00BFA5' }}
                        />
                        <Statistic 
                          title={<Space><SwapOutlined /> 发送/花费 BNB</Space>} 
                          value={stat.total_value_bnb} 
                          precision={6}
                          suffix="BNB"
                          valueStyle={{ color: '#FFC107' }}
                        />
                        <Statistic 
                          title={<Space><FireOutlined /> 总消耗 Gas (BNB)</Space>}
                          value={stat.total_gas_bnb} 
                          precision={6}
                          suffix="BNB"
                          valueStyle={{ color: '#F44336' }}
                        />
                      </Space>

                      {stat.received_tokens && Object.keys(stat.received_tokens).length > 0 && (
                        <Card
                          size="small"
                          title={<Text strong style={{color: '#00A8E8'}}>当日汇总代币 (接收/买入)</Text>}
                          bordered={false}
                          style={{ marginBottom: 16, background: 'rgba(0,0,0,0.1)' }}
                        >
                          <Space wrap>
                            {Object.entries(stat.received_tokens).map(([symbol, total]) => (
                               <Tooltip key={symbol} title={`${symbol}: ${typeof total === 'number' ? total.toFixed(8) : total}`}>
                                <Tag 
                                  color="processing"
                                  style={{ padding: '4px 10px', fontSize: '0.9em' }}
                                >
                                  {symbol}: {typeof total === 'number' ? total.toFixed(4) : (total || 'N/A')}
                                </Tag>
                               </Tooltip>
                            ))}
                          </Space>
                        </Card>
                      )}

                      <Table
                        dataSource={stat.transactions}
                        columns={[
                          { 
                            title: "交易哈希", 
                            dataIndex: "hash", 
                            width: '18%',
                            ellipsis: true,
                            render: hash => (
                              <Tooltip title={hash}>
                                <a 
                                  href={`https://bscscan.com/tx/${hash}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: '#00A8E8' }}
                                >
                                  {`${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`}
                                </a>
                              </Tooltip>
                            )
                          },
                          { 
                            title: "方法/类型", 
                            dataIndex: "method",
                            width: '15%',
                            ellipsis: true,
                            render: method => <Tag color="geekblue">{method || '未知操作'}</Tag>
                          },
                          {
                            title: "代币净值变化 (USD)",
                            key: "net_token_value_change_usd",
                            width: '18%',
                            align: 'right',
                            render: (_, record) => {
                              const netChange = calculateNetTokenValueChangeForTx(record);
                              const color = netChange > 0 ? '#00BFA5' : netChange < 0 ? '#F44336' : undefined;
                              return (
                                <Tooltip title={`基于该交易的代币转入/转出计算的净值变化 (不含BNB及Gas)`}>
                                  <Text style={{ color, fontWeight: 'bold' }}>
                                    {netChange > 0 ? '+' : ''}{formatLargeNumber(netChange)}
                                  </Text>
                                </Tooltip>
                              );
                            }
                          },
                          { 
                            title: "BNB 数量", 
                            dataIndex: "value_bnb",
                            width: '12%',
                            align: 'right',
                            render: val => (parseFloat(val) > 0 && !isNaN(parseFloat(val))) ? (
                              <Text type="warning">{parseFloat(val).toFixed(6)}</Text>
                            ) : <Text type="secondary">-</Text>
                          },
                          { 
                            title: "Gas (BNB)", 
                            dataIndex: "gas_cost_bnb",
                            width: '12%',
                            align: 'right',
                            render: val => (parseFloat(val) > 0 && !isNaN(parseFloat(val))) ? (
                              <Text type="secondary">{parseFloat(val).toFixed(6)}</Text>
                            ) : <Text type="secondary">-</Text>
                          },
                          {
                            title: "状态",
                            dataIndex: "status",
                            width: '10%',
                            align: 'center',
                            render: (status, record) => {
                               const isSuccess = record.success !== undefined ? record.success : 
                                                 (record.error ? false : true); 
                               return isSuccess ? 
                                <Tag icon={<CheckCircleOutlined />} color="success">成功</Tag> : 
                                <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>;
                            }
                          }
                        ]}
                        rowKey="hash"
                        size="small"
                        pagination={{ pageSize: 5, size: "small", simple: true, showSizeChanger: false }}
                        expandable={{
                          expandedRowRender: (record) =>
                            record.token_transfers && record.token_transfers.length > 0
                              ? renderTokenTransfers(record.token_transfers)
                              : <Text type="secondary" style={{paddingLeft: 16, display: 'block'}}>此交易无代币转账记录。</Text>,
                          expandIcon: ({ expanded, onExpand, record }) =>
                            expanded ? (
                              <Tooltip title="收起代币详情"><CloseCircleOutlined onClick={e => {e.stopPropagation(); onExpand(record, e);}} style={{color: '#F44336'}}/></Tooltip>
                            ) : (
                              <Tooltip title="查看代币详情"><InfoCircleOutlined onClick={e => {e.stopPropagation(); onExpand(record, e);}} style={{color: '#00A8E8'}}/></Tooltip>
                            )
                        }}
                        scroll={{ x: 900 }} 
                      />
                    </Panel>
                  );
                })}
              </Collapse>
            )}
          </Card>
        );
      })}
    </div>
  );
}
